import React, { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import axiosInstance from "@/utils/appsettings";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useStripeOAuthConnect } from "@/hooks/useStripeOAuthConnect";

interface ProjectOption {
  id: string;
  name: string;
}

// /protected/projects rejects limit > 100 with a 422, so page through instead of asking for everything at once
const PAGE_SIZE = 100;
const MAX_PAGES = 10;

// Stripe Connect writes the keys into one project's .env, so outside a project the user picks which one first
const StripeProjectPicker: React.FC<{ onConnected?: () => void }> = ({ onConnected }) => {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id;
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [connectedName, setConnectedName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAllProjects = async () => {
      const all: ProjectOption[] = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
        if (workspaceId) params.set("workspace_id", String(workspaceId));
        const { data } = await axiosInstance.get(`/protected/projects?${params}`);
        const batch: { id?: string; project_id?: string; name?: string; project_name?: string }[] = data?.projects ?? [];
        all.push(...batch.map((p) => ({
          id: String(p.id ?? p.project_id ?? ""),
          name: p.name || p.project_name || "Untitled project",
        })).filter((p) => p.id));
        if (batch.length < PAGE_SIZE || (typeof data?.total === "number" && (page + 1) * PAGE_SIZE >= data.total)) break;
      }
      return all;
    };
    setLoading(true);
    setError(null);
    fetchAllProjects()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        if (list.length === 1) setProjectId(list[0].id);
      })
      .catch(() => !cancelled && setError("Couldn't load your projects. Try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const selectedName = projects.find((p) => p.id === projectId)?.name ?? "";

  const { connecting, connect } = useStripeOAuthConnect(projectId || null, {
    onConnected: () => {
      setError(null);
      setConnectedName(selectedName);
      onConnected?.();
    },
    onFailed: (err) => setError(err || "Could not connect your Stripe account. Try again."),
    onPopupBlocked: () => setError("Allow popups for this site, then try again."),
    onOpenError: () => setError("Could not open Stripe authorisation. Try again."),
  });

  return (
    <section className="rounded-xl border border-line bg-surface p-3.5">
      <h4 className="text-sm font-semibold text-foreground">Choose a project</h4>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Stripe connects to one project at a time. Pick where payments should go, then finish on Stripe.
      </p>

      {loading ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your projects…
        </p>
      ) : projects.length === 0 && !error ? (
        <p className="mt-3 text-xs text-muted-foreground">You don't have a project yet. Create one, then connect Stripe to it.</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <label className="sr-only" htmlFor="stripe-project">Project</label>
          <select
            id="stripe-project"
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setConnectedName(null);
              setError(null);
            }}
            className="h-9 min-w-0 flex-1 rounded-[9px] border border-input bg-surface px-2.5 text-[13px] text-foreground focus:border-plum/40 focus:outline-none"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!projectId}
            // connect() opens the popup synchronously, so it must run straight from this click
            onClick={() => {
              setError(null);
              void connect();
            }}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-[9px] bg-gradient-sunset px-3.5 text-[13px] font-semibold text-white transition-shadow hover:shadow-[0_4px_14px_#a23c9133] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {connecting ? "Waiting for Stripe…" : "Connect Stripe"}
            {!connecting && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}

      {connectedName && (
        <p className="mt-2.5 flex items-center gap-1.5 text-xs font-medium text-success">
          <CheckCircle2 className="h-3.5 w-3.5" /> Stripe connected to {connectedName}. Its keys are in the project's environment.
        </p>
      )}
      {error && <p className="mt-2.5 text-xs text-danger">{error}</p>}
    </section>
  );
};

export default StripeProjectPicker;
